import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Search, Filter, Download, RefreshCw, Calendar,
  User, Activity, Upload, Database, Shield, Bot, MessageSquare,
  CheckCircle, XCircle, AlertCircle, Clock, ChevronLeft, ChevronRight,
  Eye, Trash2, Edit, UserPlus, LogIn, LogOut, Key, Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Operation type icons and colors
const OPERATION_CONFIG = {
  CREATE: { icon: UserPlus, color: 'bg-green-100 text-green-700', label: 'Create' },
  READ: { icon: Eye, color: 'bg-blue-100 text-blue-700', label: 'Read' },
  UPDATE: { icon: Edit, color: 'bg-amber-100 text-amber-700', label: 'Update' },
  DELETE: { icon: Trash2, color: 'bg-red-100 text-red-700', label: 'Delete' },
  UPLOAD: { icon: Upload, color: 'bg-purple-100 text-purple-700', label: 'Upload' },
  EXECUTE: { icon: Bot, color: 'bg-indigo-100 text-indigo-700', label: 'Execute' },
  AUTH: { icon: Key, color: 'bg-slate-100 text-slate-700', label: 'Auth' },
  LOGIN: { icon: LogIn, color: 'bg-green-100 text-green-700', label: 'Login' },
  LOGOUT: { icon: LogOut, color: 'bg-slate-100 text-slate-700', label: 'Logout' }
};

// Resource type icons
const RESOURCE_ICONS = {
  users: User,
  companies: Database,
  uploads: Upload,
  data_upload: Upload,
  agent: Bot,
  chat: MessageSquare,
  authentication: Key,
  settings: Settings
};

const AuditLog = () => {
  const { user } = useAuth();
  const { languageConfig } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    operationType: '',
    resourceType: '',
    userId: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    loadLogs();
    loadSummary();
  }, [pagination.page, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        skip: ((pagination.page - 1) * pagination.limit).toString()
      });
      
      if (filters.operationType) params.append('operation_type', filters.operationType);
      if (filters.resourceType) params.append('resource_type', filters.resourceType);
      if (filters.userId) params.append('user_id', filters.userId);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      // Fallback to mock data for demo
      setLogs(generateMockLogs());
      setPagination(prev => ({ ...prev, total: 50 }));
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/audit-logs/summary?days=7`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSummary(response.data);
    } catch (error) {
      // Fallback summary
      setSummary({
        period_days: 7,
        operation_counts: { CREATE: 15, UPDATE: 42, DELETE: 3, UPLOAD: 28, EXECUTE: 156, AUTH: 89 },
        upload_stats: { totalUploads: 28, totalRows: 15420, successfulRows: 15180, failedRows: 240 }
      });
    }
  };

  // Generate mock logs for demo
  const generateMockLogs = () => {
    const operations = ['CREATE', 'UPDATE', 'DELETE', 'UPLOAD', 'EXECUTE', 'AUTH'];
    const resources = ['users', 'data_upload', 'agent', 'authentication', 'settings'];
    const users = ['admin@reasoniq.com', 'manager@reasoniq.com', 'analyst@reasoniq.com'];
    const agents = ['StockSense', 'SalesIQ', 'Customer360', 'ProductMix', 'ExecutiveBrain'];
    
    return Array.from({ length: 20 }, (_, i) => {
      const operation = operations[Math.floor(Math.random() * operations.length)];
      const resource = resources[Math.floor(Math.random() * resources.length)];
      const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);
      
      return {
        auditId: `audit_${Date.now()}_${i}`,
        tenantId: 'TENANT-001',
        userId: users[Math.floor(Math.random() * users.length)],
        userRole: ['admin', 'manager', 'analyst'][Math.floor(Math.random() * 3)],
        operationType: operation,
        resourceType: resource,
        resourceId: `res_${Math.random().toString(36).substr(2, 9)}`,
        action: operation === 'EXECUTE' 
          ? `Execute agent ${agents[Math.floor(Math.random() * agents.length)]}`
          : operation === 'UPLOAD'
          ? 'Upload res_sales: sales_jan2026.csv (5420 rows)'
          : operation === 'AUTH'
          ? 'login_success'
          : `${operation} operation on ${resource}`,
        status: Math.random() > 0.1 ? 'success' : 'failure',
        timestamp: timestamp.toISOString(),
        details: operation === 'UPLOAD' ? {
          filename: 'sales_data.csv',
          totalRows: 5420,
          successfulRows: 5380,
          failedRows: 40
        } : {}
      };
    }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const handleExport = async () => {
    try {
      // Generate CSV
      const headers = ['Timestamp', 'User', 'Role', 'Operation', 'Resource', 'Action', 'Status'];
      const rows = logs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.userId,
        log.userRole || 'N/A',
        log.operationType,
        log.resourceType,
        log.action,
        log.status
      ]);
      
      const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Audit log exported successfully');
    } catch (error) {
      toast.error('Failed to export audit log');
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const getOperationIcon = (type) => {
    const config = OPERATION_CONFIG[type] || OPERATION_CONFIG.READ;
    const Icon = config.icon;
    return <Icon className="h-4 w-4" />;
  };

  const getResourceIcon = (type) => {
    const Icon = RESOURCE_ICONS[type] || Activity;
    return <Icon className="h-4 w-4 text-slate-400" />;
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="h-7 w-7 text-blue-600" />
            <TranslatedText text="Audit Log" cacheKey="audit:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Track all system activities and admin actions" cacheKey="audit:subtitle" />
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadLogs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleExport} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.entries(summary.operation_counts || {}).slice(0, 6).map(([op, count]) => {
            const config = OPERATION_CONFIG[op] || OPERATION_CONFIG.READ;
            const Icon = config.icon;
            return (
              <Card key={op} className="bg-white dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 rounded-lg ${config.color.split(' ')[0]} flex items-center justify-center`}>
                      <Icon className={`h-4 w-4 ${config.color.split(' ')[1]}`} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">{count}</span>
                  </div>
                  <p className="text-xs text-slate-500">{config.label} (7d)</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Operation Type</label>
              <select
                value={filters.operationType}
                onChange={(e) => setFilters({ ...filters, operationType: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Operations</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="UPLOAD">Upload</option>
                <option value="EXECUTE">Execute</option>
                <option value="AUTH">Authentication</option>
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Resource Type</label>
              <select
                value={filters.resourceType}
                onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Resources</option>
                <option value="users">Users</option>
                <option value="data_upload">Data Uploads</option>
                <option value="agent">AI Agents</option>
                <option value="authentication">Authentication</option>
                <option value="settings">Settings</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Search User</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                  placeholder="Search by user email..."
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setFilters({ operationType: '', resourceType: '', userId: '', startDate: '', endDate: '' })}
              className="text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader className="border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Activity Log</CardTitle>
            <span className="text-sm text-slate-500">{pagination.total} total entries</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Timestamp</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Operation</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resource</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {logs.map((log, idx) => {
                    const opConfig = OPERATION_CONFIG[log.operationType] || OPERATION_CONFIG.READ;
                    const OpIcon = opConfig.icon;
                    
                    return (
                      <tr 
                        key={log.auditId || idx} 
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-slate-400" />
                            <div>
                              <p className="text-sm text-slate-900 dark:text-white">
                                {new Date(log.timestamp).toLocaleDateString('en-IN')}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(log.timestamp).toLocaleTimeString('en-IN')}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                              <User className="h-4 w-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="text-sm text-slate-900 dark:text-white truncate max-w-[150px]">
                                {log.userId}
                              </p>
                              <Badge variant="outline" className="text-xs mt-0.5">
                                {log.userRole || 'user'}
                              </Badge>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={opConfig.color}>
                            <OpIcon className="h-3 w-3 mr-1" />
                            {opConfig.label}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getResourceIcon(log.resourceType)}
                            <span className="text-sm text-slate-600 dark:text-slate-300 capitalize">
                              {log.resourceType?.replace(/_/g, ' ') || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-slate-700 dark:text-slate-200 max-w-[250px] truncate" title={log.action}>
                            {log.action}
                          </p>
                        </td>
                        <td className="p-4">
                          <Badge 
                            variant="outline"
                            className={log.status === 'success' 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : log.status === 'partial'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                            }
                          >
                            {log.status === 'success' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {log.status === 'failure' && <XCircle className="h-3 w-3 mr-1" />}
                            {log.status === 'partial' && <AlertCircle className="h-3 w-3 mr-1" />}
                            {log.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        
        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">
                Page {pagination.page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLog(null)}>
          <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Audit Log Details</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedLog(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Audit ID</p>
                  <p className="text-sm font-mono text-slate-800">{selectedLog.auditId}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Timestamp</p>
                  <p className="text-sm text-slate-800">{new Date(selectedLog.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">User</p>
                  <p className="text-sm text-slate-800">{selectedLog.userId}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Role</p>
                  <Badge variant="outline">{selectedLog.userRole || 'N/A'}</Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Operation</p>
                  <Badge className={OPERATION_CONFIG[selectedLog.operationType]?.color || ''}>
                    {selectedLog.operationType}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <Badge variant="outline" className={selectedLog.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
                    {selectedLog.status}
                  </Badge>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Action</p>
                <p className="text-sm text-slate-800 bg-slate-50 p-3 rounded-lg">{selectedLog.action}</p>
              </div>
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Additional Details</p>
                  <pre className="text-xs text-slate-700 bg-slate-50 p-3 rounded-lg overflow-auto max-h-40">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedLog.errorMessage && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Error Message</p>
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{selectedLog.errorMessage}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
